import * as Notifications from 'expo-notifications';
import React, { useEffect } from 'react';
import { AccountNotificationsScreen } from '@/screens/account-notifications-screen';
import { AccountProfileScreen } from '@/screens/account-profile-screen';
import { AdsContextProvider } from '@/contexts/ads-context';
import { APP_CONTAINER_ROUTES } from './routes';
import { AppContainerStackParams, AppModalStackParams } from './types';
import { AppTabs } from './app-tabs';
import { BrandPageScreen } from '@/screens/brand-page/brand-page-screen';
import { createStackNavigator } from '@react-navigation/stack';
import { CreditMemoDetailScreen } from '@/screens/credit-memo-detail/credit-memo-detail-screen';
import { CreditRequestDetailScreen } from '@/screens/credit-request/credit-request-detail-screen';
import { CreditRequestInvoiceStack } from '@/navigators/credit-request-invoice-stack';
import { CustomTagPrintScreen } from '@/screens/partners/custom-tag-print-screen';
import { DisplayAdDetailScreen } from '@/components/ads/display-ad-detail-screen';
import { DrawerHeaderLarge } from '@/components/drawer-navigation/drawer-header-large';
import { EmployeeStack } from './employee-stack';
import { ExternalContentProvider } from '@/contexts/external-content-context';
import { ExternalContentScreen } from '@/screens/links/external-content-screen';
import { InsightsHubStack } from './insights-hub-stack';
import { InvoiceDetailScreen } from '@/screens/invoice-detail/invoice-detail-screen';
import { ItemDetailContextProvider } from '@/contexts/item-detail-context';
import { ItemLeadTimeContextProvider } from '@/contexts/item-lead-time-context';
import { ItemRecommendationsContextProvider } from '@/contexts/item-recommendations-context';
import { MenuInfoStack } from './menu-info-stack';
import { MultiOrderGuideItemsContextProvider } from '@/contexts/multi-order-guide-items-context-provider';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { NavigationStateRoute } from '@/utils/navigation';
import { NewOrderStack } from './new-order-stack';
import { NotificationPermissionsActionSheet } from '@/components/notification-permissions-action-sheet';
import { OrderCheckInStack } from './check-in-stack';
import { OrderDetailScreen } from '@/screens/order-detail/order-detail-screen';
import { OrderDrawerContextProvider } from '@/contexts/order-drawer-context';
import { OriginalInboundDetailScreen } from '@/screens/order-inbounds/original-inbound-detail-screen';
import { PaymentDetailScreen } from '@/screens/payments/payment-detail-screen';
import { PaymentLinkDetailScreen } from '@/screens/payments/payment-link-detail-screen';
import { RestaurantAccountScreen } from '@/screens/restaurant/restaurant-account-screen';
import { ShortPayCreditRequestStack } from '@/navigators/short-pay-credit-request-stack';
import { StorefrontBootIntent } from '@domain/microproducts/boot-params-types';
import { StorefrontMicroProductScreen } from '@/screens/storefront-microproducts/storefront-micro-product-screen';
import { StyleSheet, View } from 'react-native';
import { TagPrintBatchStack } from './tag-print-batch-stack';
import { UpdateOrderGuideItemContextProvider } from '@/contexts/update-order-guide-item-context';
import { useActionSheetManagerContext } from '@/contexts/action-sheet-manager-context';
import { useDefaultStackScreenOptions } from '@/hooks/use-default-stack-screen-options';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNotificationListener } from '@/hooks/use-notification-listener';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useStorefrontBootNavigation } from '@/hooks/use-storefront-boot-navigation';
import { VolumePromotionsContextProvider } from '@/contexts/volume-promotions-context';

const Stack = createStackNavigator<AppContainerStackParams>();

interface Props {
  canShowNotificationSheet: boolean;
  clearPendingStorefrontNavigation: () => void;
  lastActiveTabRouteRef: React.RefObject<NavigationStateRoute | null>;
  lastProcessedNotificationID: string | null;
  onActionSheetShown: () => void;
  pendingStorefrontNavigation: StorefrontBootIntent | null;
  setLastProcessedNotificationID: (nextID: string) => void;
}

export const AppTabsContainerStack: React.FC<Props> = ({
  canShowNotificationSheet,
  clearPendingStorefrontNavigation,
  lastActiveTabRouteRef,
  lastProcessedNotificationID,
  onActionSheetShown,
  pendingStorefrontNavigation,
  setLastProcessedNotificationID,
}) => {
  const navigation = useNavigation<NavigationProp<AppModalStackParams>>();
  const { showActionSheet } = useActionSheetManagerContext();
  const screenOptions = useDefaultStackScreenOptions();
  const { isSmallLayout, isLargeLayout } = useLayoutContext();
  const isOfflineModeActive = useOfflineModeActive();

  useNotificationListener(
    lastProcessedNotificationID,
    setLastProcessedNotificationID,
  );

  // Navigation half of the storefront boot-URL intent: lives inside the
  // restaurant-keyed subtree so it (re)runs with live navigators after the
  // restaurant switch; the latch state itself is held above the key in
  // AppStack (the lastProcessedNotificationID pattern).
  useStorefrontBootNavigation(
    pendingStorefrontNavigation,
    clearPendingStorefrontNavigation,
  );

  useEffect(() => {
    // Notification permissions should only be requested after signing in
    // or upon completing the onboarding flow
    // or when the user is not in offline mode
    if (!canShowNotificationSheet || isOfflineModeActive) {
      return;
    }

    (async () => {
      const { canAskAgain, status } = await Notifications.getPermissionsAsync();
      if (status !== 'granted' && canAskAgain) {
        showActionSheet(NotificationPermissionsActionSheet);
        onActionSheetShown();
      }
    })();
    // Check notification permissions only on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = (
    <Stack.Navigator screenOptions={screenOptions}>
      <Stack.Screen
        children={() => (
          <AppTabs lastActiveTabRouteRef={lastActiveTabRouteRef} />
        )}
        name={APP_CONTAINER_ROUTES.Base}
        // Prevent navigation animation when switching chats
        options={{ animationEnabled: false }}
      />
      <Stack.Screen
        component={BrandPageScreen}
        name={APP_CONTAINER_ROUTES.BrandPage}
      />
      <Stack.Screen
        component={CreditMemoDetailScreen}
        name={APP_CONTAINER_ROUTES.CreditMemoDetail}
      />
      <Stack.Screen
        component={CreditRequestDetailScreen}
        name={APP_CONTAINER_ROUTES.CreditRequestDetail}
      />
      <Stack.Screen
        component={CreditRequestInvoiceStack}
        name={APP_CONTAINER_ROUTES.CreditRequestInvoiceItems}
      />
      <Stack.Screen
        component={DisplayAdDetailScreen}
        name={APP_CONTAINER_ROUTES.DisplayAd}
      />
      <Stack.Screen
        component={InvoiceDetailScreen}
        name={APP_CONTAINER_ROUTES.InvoiceDetail}
      />
      <Stack.Screen
        component={NewOrderStack}
        name={APP_CONTAINER_ROUTES.NewOrder}
      />
      <Stack.Screen
        component={OrderCheckInStack}
        name={APP_CONTAINER_ROUTES.OrderCheckIn}
      />
      <Stack.Screen
        component={OrderDetailScreen}
        name={APP_CONTAINER_ROUTES.OrderDetail}
      />
      <Stack.Screen
        component={PaymentLinkDetailScreen}
        name={APP_CONTAINER_ROUTES.PaymentLinkDetail}
      />
      <Stack.Screen
        component={PaymentDetailScreen}
        name={APP_CONTAINER_ROUTES.PaymentDetail}
      />
      <Stack.Screen
        component={RestaurantAccountScreen}
        name={APP_CONTAINER_ROUTES.Account}
      />
      <Stack.Screen
        component={AccountProfileScreen}
        name={APP_CONTAINER_ROUTES.Profile}
      />
      <Stack.Screen
        component={AccountNotificationsScreen}
        name={APP_CONTAINER_ROUTES.AccountNotificationSettings}
      />
      <Stack.Screen
        component={EmployeeStack}
        name={APP_CONTAINER_ROUTES.Employees}
      />
      <Stack.Screen
        component={InsightsHubStack}
        name={APP_CONTAINER_ROUTES.InsightsHub}
      />
      <Stack.Screen
        component={MenuInfoStack}
        name={APP_CONTAINER_ROUTES.MenuInfo}
      />
      <Stack.Screen
        component={ExternalContentScreen}
        name={APP_CONTAINER_ROUTES.Links}
      />
      <Stack.Screen
        component={OriginalInboundDetailScreen}
        name={APP_CONTAINER_ROUTES.OriginalInbound}
      />
      <Stack.Screen
        component={ShortPayCreditRequestStack}
        name={APP_CONTAINER_ROUTES.ShortPayCreditRequestItems}
      />
      {/* Small-layout surface for storefront microproducts. Large/tablet
          renders in-container via the hidden app-tabs tab instead
          (lazy:true so nothing eager-mounts); this pushed screen remains for
          the small layout, which has no tab entry — the same split as
          Links/ExternalContentScreen. */}
      <Stack.Screen
        component={StorefrontMicroProductScreen}
        name={APP_CONTAINER_ROUTES.StorefrontMicroProduct}
      />
      <Stack.Screen
        component={TagPrintBatchStack}
        name={APP_CONTAINER_ROUTES.TagPrintBatch}
      />
      <Stack.Screen
        component={CustomTagPrintScreen}
        name={APP_CONTAINER_ROUTES.CustomTagPrint}
      />
    </Stack.Navigator>
  );

  return (
    <AdsContextProvider>
      <ItemLeadTimeContextProvider>
        <VolumePromotionsContextProvider>
          <MultiOrderGuideItemsContextProvider>
            <ItemRecommendationsContextProvider>
              <ItemDetailContextProvider>
                <UpdateOrderGuideItemContextProvider navigation={navigation}>
                  <ExternalContentProvider>
                    {isSmallLayout ? (
                      content
                    ) : (
                      <OrderDrawerContextProvider>
                        <View style={styles.largeWrapper}>
                          {content}
                          {isLargeLayout ? <DrawerHeaderLarge /> : null}
                        </View>
                      </OrderDrawerContextProvider>
                    )}
                  </ExternalContentProvider>
                </UpdateOrderGuideItemContextProvider>
              </ItemDetailContextProvider>
            </ItemRecommendationsContextProvider>
          </MultiOrderGuideItemsContextProvider>
        </VolumePromotionsContextProvider>
      </ItemLeadTimeContextProvider>
    </AdsContextProvider>
  );
};

const styles = StyleSheet.create({
  largeWrapper: {
    flex: 1,
    flexDirection: 'column-reverse',
  },
});
